/**
 * Settings' Linked Sign-Ins (issue #50, stage C): another address's sign-in
 * joined to this one, so either opens every business of both, from the
 * profile menu's picker.
 *
 * WHAT THE FORM ASKS FOR, AND WHY ALL FOUR. A link hands this login the other
 * login's books, so it is proved the way signing in to that address is -- its
 * password and a current code from its authenticator -- and it changes THIS
 * login, so a code from this login's authenticator (or one of its recovery
 * codes) is spent too. The server says which of them was wrong, in a sentence;
 * an address that does not sign in gets the same sentence as a wrong password.
 *
 * WHAT IT DOES NOT SHOW is the other login's businesses. They appear in the
 * profile menu once linked, which is where a business is chosen; this section
 * is about sign-ins.
 *
 * After a link or an unlink the session is read again (`refresh`), so the
 * profile menu's list changes without a reload.
 *
 * Read on its own, as Business Details is, so a failure here leaves the rest
 * of Settings working and is said in this section alone.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type LinkedLogin } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { formatTimestamp } from '../lib/timezone.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

type Step = 'idle' | 'link' | 'leave';

const EMPTY_LINK = { email: '', password: '', theirCode: '', code: '' };

export function LinkedSignInsSection() {
  const { refresh } = useAuth();
  const [linked, setLinked] = useState<LinkedLogin[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>('idle');
  const [form, setForm] = useState(EMPTY_LINK);
  const [leaveCode, setLeaveCode] = useState('');

  useEffect(() => {
    let live = true;
    api
      .listLinkedLogins()
      .then((result) => live && setLinked(result.linked))
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the linked sign-ins.');
      });
    return () => {
      live = false;
    };
  }, []);

  function reset() {
    setStep('idle');
    setForm(EMPTY_LINK);
    setLeaveCode('');
    setError(null);
  }

  async function submitLink() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.linkLogin(form);
      setLinked(result.linked);
      setNotice(`Linked to ${form.email.trim()}`);
      reset();
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not link that sign-in.');
    } finally {
      setBusy(false);
    }
  }

  async function submitLeave() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.leaveLink(leaveCode);
      setLinked([]);
      setNotice('This sign-in is no longer linked');
      reset();
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not unlink this sign-in.');
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof typeof EMPTY_LINK, label: string, extra: { type?: string; autoComplete?: string } = {}) => (
    <label className="field">
      <span>{label}</span>
      <input
        type={extra.type ?? 'text'}
        autoComplete={extra.autoComplete ?? 'off'}
        value={form[key]}
        onChange={(event) => {
          const value = event.target.value;
          setForm((current) => ({ ...current, [key]: value }));
        }}
      />
    </label>
  );

  return (
    <section className="settings-section" aria-labelledby="settings-linked">
      <h2 id="settings-linked">Linked Sign-Ins</h2>
      <p className="muted">
        Another address&apos;s sign-in, linked to this one, so either opens the businesses of both.{' '}
        <InfoNote label="What A Link Does">
          Once linked, the profile menu lists every business of both sign-ins, and either can switch between them.
          In a business the other address belongs to, what you record is recorded under that address, which is the
          one that business knows. Linking joins this sign-in to that address AND every sign-in already linked to it,
          so all of them open all of their businesses. Each keeps its own password, authenticator and recovery
          codes, and the link is only as safe as the weakest of them: anyone who can sign in as one can open them
          all. Any sign-in can unlink itself at any time, which ends every session that was standing in the
          others&apos; businesses.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {linked === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : linked.length === 0 ? (
        <p className="muted">No other sign-in is linked to this one.</p>
      ) : (
        <ul className="linked-sign-ins">
          {linked.map((row) => (
            <li key={row.email}>
              <strong>{row.email}</strong>
              {row.linkedAt && <span className="muted"> — linked {formatTimestamp(row.linkedAt)}</span>}
            </li>
          ))}
        </ul>
      )}

      {linked !== null && step === 'idle' && (
        <div className="row">
          <button type="button" disabled={busy} onClick={() => { reset(); setStep('link'); }}>
            Link Another Sign-In
          </button>
          {linked.length > 0 && (
            <button type="button" disabled={busy} onClick={() => { reset(); setStep('leave'); }}>
              Unlink This Sign-In
            </button>
          )}
        </div>
      )}

      {step === 'link' && (
        <div className="new-account undo-form authenticator-form" role="group" aria-label="Link Another Sign-In">
          <p>
            The other address&apos;s password and a code from its authenticator prove it is yours to link. Any
            sign-in already linked to it joins too.
          </p>
          {field('email', 'Their Email', { type: 'email' })}
          {field('password', 'Their Password', { type: 'password', autoComplete: 'off' })}
          {field('theirCode', 'Code From Their Authenticator', { autoComplete: 'one-time-code' })}
          {field('code', 'Your Code Or A Recovery Code', { autoComplete: 'one-time-code' })}
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void submitLink()}>
              {busy ? 'Checking…' : 'Link Them'}
            </button>
            <button type="button" className="link" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'leave' && (
        <div className="new-account undo-form authenticator-form" role="group" aria-label="Unlink This Sign-In">
          <p>
            This sign-in leaves the link; any others stay linked to each other. Sessions standing in the other
            sign-ins&apos; businesses end, and this one comes back to a business of your own.
          </p>
          <label className="field">
            <span>Your Code Or A Recovery Code</span>
            <input
              autoComplete="one-time-code"
              value={leaveCode}
              onChange={(event) => setLeaveCode(event.target.value)}
            />
          </label>
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void submitLeave()}>
              {busy ? 'Checking…' : 'Unlink'}
            </button>
            <button type="button" className="link" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
